import { memo, useRef, useState } from "react";

// mui components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import OutlinedInput from "@mui/material/OutlinedInput";
import Chip from "@mui/material/Chip";
import LoadingButton from "@mui/lab/LoadingButton";
import Stack from "@mui/material/Stack";
import { Icon } from "@iconify/react";
import { useMutation } from "@tanstack/react-query";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
// constants, data and custom components
import Map from "./Map";
import { issueImgUpload } from "src/components/ServiceRequest/serviceRequest.hook";
import { fileToBase64 } from "src/utils/fileConvertBase64";
import { AMENITIES } from "../../components/charger/charger.constants";
import { is } from "src/util/is";
import { isValidImageType, MAX_IMAGE_SIZE_MB } from "src/utils/fileTypes";
import { alertSlice } from "src/store/alert/alert.slice";
import { useDispatch } from "react-redux";
import enums from "src/util/enums";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function Address({ touched,errors, getFieldProps, setFieldValue, formValues, hide = false,notDisplayTitle=false,displayedFor, isReadOnly = false }) {
  const imageUploadInputRef = useRef();
  const dispatch = useDispatch();
  const { actions: { setAlert } } = alertSlice;
  const imgUpload = useMutation(issueImgUpload);
  const [imageUploadPending, setImageUploadPending] = useState(false);

  const readAddressComponent = (components = [], type, format = "long") => {
    const component = components.find((item) => item?.types?.includes(type));
    if (!component) return "";
    return format === "short" ? component.short_name : component.long_name;
  };

  const uniqueJoin = (values = [], separator = ", ") => {
    const seen = new Set();
    const result = [];
    values
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean)
      .forEach((value) => {
        const key = value.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        result.push(value);
      });
    return result.join(separator);
  };

  const handlePlacesChanged = (places) => {
    if (!Array.isArray(places) || !places.length || !places[0]) {
      return;
    }

    const {
      address_components = [],
      geometry: { location },
      formatted_address,
    } = places[0];

    const streetNumber = readAddressComponent(address_components, "street_number");
    const route = readAddressComponent(address_components, "route");
    const subPremise = readAddressComponent(address_components, "subpremise");
    const premise = readAddressComponent(address_components, "premise");
    const neighborhood = readAddressComponent(address_components, "neighborhood");
    const subLocality1 = readAddressComponent(address_components, "sublocality_level_1");
    const subLocality2 = readAddressComponent(address_components, "sublocality_level_2");
    const landmark =
      readAddressComponent(address_components, "point_of_interest") ||
      readAddressComponent(address_components, "establishment");
    const locality = readAddressComponent(address_components, "locality");
    const postalTown = readAddressComponent(address_components, "postal_town");
    const adminLevel3 = readAddressComponent(address_components, "administrative_area_level_3");
    const adminLevel2 = readAddressComponent(address_components, "administrative_area_level_2");
    const adminLevel1 = readAddressComponent(address_components, "administrative_area_level_1");

    const state = adminLevel1 || adminLevel2;
    // City: prefer the administrative city/district (e.g. "Saharanpur") over a
    // village-level "locality" name. admin_area_level_2 is the district in India.
    const city =
      adminLevel3 ||
      locality ||
      postalTown ||
      adminLevel2 ||
      subLocality1;
    // Avoid city duplicating state when both fell back to the same admin area.
    const resolvedCity = city && city === state ? "" : city;
    const country = readAddressComponent(address_components, "country");
    const postalCode = readAddressComponent(address_components, "postal_code");

    const buildingNo = premise || subPremise;
    const houseNo = streetNumber;

    // Line 1: Building/House No + Street (e.g. "Plot No 12, MG Road")
    const addressLine1 =
      uniqueJoin([houseNo, buildingNo, route]) ||
      uniqueJoin([buildingNo, route]) ||
      formatted_address ||
      "";

    // Line 2: Area / Locality / Sector (e.g. "Sector 62")
    const addressLine2 = uniqueJoin([subLocality2, subLocality1, neighborhood, locality]);

    // Line 3: Landmark / additional details (e.g. "Near Metro Station Gate 2")
    const addressLine3 = uniqueJoin([landmark]);

    if (address_components.length) {
      setFieldValue("street", addressLine1);
      setFieldValue(houseNumberField, addressLine2);
      setFieldValue("addressLine3", addressLine3);
      setFieldValue("city", resolvedCity || "");
      setFieldValue("state", state || "");
      setFieldValue("country", country || "");
      setFieldValue("zipCode", postalCode || "");
      setFieldValue("locationLatitude", location.lat());
      setFieldValue("locationLongitude", location.lng());
    } else {
      setFieldValue("street", formatted_address || "");
      setFieldValue(houseNumberField, "");
      setFieldValue("addressLine3", "");
      setFieldValue("city", "");
      setFieldValue("state", "");
      setFieldValue("country", "");
      setFieldValue("zipCode", "");
      setFieldValue("locationLatitude", location.lat());
      setFieldValue("locationLongitude", location.lng());
    }
  };
  const handleAmenitiesChange = (event) => {
    const {
      target: { value },
    } = event;
    setFieldValue("amenities",
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    ); 
  }

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    const validFiles = [];
    setImageUploadPending(true);
    const invalidFiles = [];
    
    for (let file of files) {
      if (!isValidImageType(file.type)) {
        invalidFiles.push(file.name);
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        invalidFiles.push(`${file.name} (exceeds ${MAX_IMAGE_SIZE_MB}MB)`);
        continue;
      }
      validFiles.push({
        file: file,
        base64: await fileToBase64(file)
      });
    }

    if (invalidFiles.length > 0) {
      dispatch(setAlert({
        open: true,
        message: `${invalidFiles.join(", ")} this file type is invalid, or use png, webp, jpeg, jpg, svg etc`,
        severity: enums.SEVERITY.ERROR,
      }));
      setImageUploadPending(false);
      return;
    }

    e.target.value = ""; // Clear the input to allow re-upload of the same files

    validFiles.forEach(({ file, base64 }) => {
      imgUpload.mutate(
        {
          image: file,
          folder: "station"
        },
        {
          onSuccess: (response) => {
            // console.log(response)
            setImageUploadPending(false);
            const { data } = response.data;
            const photos = formValues.photos.concat([data?.url]);
            setFieldValue("photos", photos);
          },
        }
      );
    })
  };

  const houseNumberField = Object.prototype.hasOwnProperty.call(formValues || {}, "house_number")
    ? "house_number"
    : "houseNumber";

  return (
    <Grid container spacing={3} className="mb-5">
      {!notDisplayTitle &&<Grid item xs={12} lg={12}>
        <Typography
          variant="subtitle1"
          gutterBottom
        >
          Address
        </Typography>
        <Divider />
      </Grid>}
      {!hide && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">
            Select Location on Map
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} lg={12}>
        <Map
          onPlaces={handlePlacesChanged}
          onMapClick={handlePlacesChanged}
          lat={formValues.locationLatitude}
          lng={formValues.locationLongitude}
          address={formValues.street}
        />
      </Grid>
      {!hide && (
        <Grid item xs={12} lg={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Latitude"
                  size="small"
                  disabled={!0}
                  helperText="Enter latitude between -90 and 90"
                  {...getFieldProps("locationLatitude")}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Longitude"
                  size="small"
                  disabled={!0}
                  helperText="Enter longitude between -180 and 180"
                  {...getFieldProps("locationLongitude")}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Address Line 1"
                  size="small"
                  required
                  error={Boolean(touched?.street && errors?.street)}
                  helperText={touched?.street && errors?.street}
                  {...getFieldProps("street")}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Address Line 2"
                  size="small"
                  {...getFieldProps(houseNumberField)}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={12}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Address Line 3"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  {...getFieldProps("addressLine3")}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="City"
                  size="small"
                  required
                  error={Boolean(touched?.city && errors?.city)}
                  helperText={touched?.city && errors?.city}
                  {...getFieldProps("city")}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="State"
                  size="small"
                  {...getFieldProps("state")}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Country"
                  size="small"
                  required
                  error={Boolean(touched?.country && errors?.country)}
                  helperText={touched?.country && errors?.country}
                  {...getFieldProps("country")}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth size="small">
                <TextField
                  label="PIN Code / Postal Code"
                  size="small"
                  required
                  error={Boolean(touched?.zipCode && errors?.zipCode)}
                  helperText={touched?.zipCode && errors?.zipCode}
                  {...getFieldProps("zipCode")}
                />
              </FormControl>
            </Grid>
           {displayedFor==="charger" &&<> <Grid item xs={12} lg={12}>
                <InputLabel id="demo-multiple-chip-label">Amenities</InputLabel>
              <FormControl fullWidth>
                <Select
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  multiple
                  value={formValues.amenities && formValues.amenities.length?formValues.amenities.filter((amenity)=>!is.empty(amenity)):[]}
                  onChange={handleAmenitiesChange}
                  input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {AMENITIES.map((am) => (
                    <MenuItem
                      key={am.value}
                      value={am.value}
                      // style={getStyles(name, personName, theme)}
                    >
                      {am.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={12}>
              <Box style={{ border: "2px solid #C4C4C4", borderRadius: 4, padding: 24 }}>
                <Stack direction="column" alignItems="center" gap={3}>
                  <Typography color="rgba(0, 0, 0, 0.60)" textAlign="center" variant="body2">You're encouraged to add a photo. It will help other users to have a better idea of the place.</Typography>
                  <Icon icon="tabler:camera-plus" width={71} height={55} color="#d9d9d9" hFlip={true} />
                  {!isReadOnly && (
                    <LoadingButton
                      variant="contained"
                      onClick={() => imageUploadInputRef.current.click()}
                      loading={imageUploadPending}
                      loadingIndicator="Loading…"
                      startIcon={<Icon icon="ic:baseline-plus"></Icon>}
                    >
                      Upload
                      <input
                        accept="image/jpeg,image/png,image/jpg"
                        type="file"
                        hidden
                        multiple
                        id="issue-image"
                        ref={imageUploadInputRef}
                        onChange={handleImageUpload}
                      />
                    </LoadingButton>
                  )}
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} lg={12}>
              {  formValues.photos && 
              <ImageList cols={3}>
                { !is.empty(formValues.photos) && formValues.photos.filter(url=>!is.empty(url)).map((url) => (
                  <ImageListItem key={`${url}-key`}>
                    <img
                      src={url}
                      alt={"attachment"}
                      style={{
                        maxHeight: "170px",
                        maxWidth: "250px",
                      }}
                      loading="eager"
                    />
                </ImageListItem>
                ))}
              </ImageList>
          }
            </Grid>
            </>
            }
          </Grid>
        </Grid>
      )}
      {["charger"].includes(displayedFor) && <>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth size="small">
          <TextField
            type="number"
            label="Operator Number 1"
            size="small"
            {...getFieldProps("operator_number1")}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth size="small">
          <TextField
            type="number"
            label="Operator Number 2"
            size="small"
            {...getFieldProps("operator_number2")}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth size="small">
          <TextField
            type="number"
            label="Operator Whatsapp"
            size="small"
            {...getFieldProps("operator_whatsapp")}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth size="small">
          <TextField
            type="email"
            label="Operator Email"
            size="small"
            {...getFieldProps("operator_email")}
            error={Boolean(touched.operator_email && errors.operator_email)}
            helperText={touched.operator_email && errors.operator_email}
          />
        </FormControl>
      </Grid>
       <Grid item xs={12} lg={6}>
        <FormControl fullWidth size="small">
          <TextField
            type="url"
            label="Operator Website"
            size="small"
            {...getFieldProps("operator_website")}
            error={Boolean(touched.operator_website && errors.operator_website)}
            helperText={touched.operator_website && errors.operator_website}
          />
        </FormControl>
      </Grid>
      
      
      </>}
    </Grid>
  );
}

export default memo(Address);
